// Grids & Gantts — a grid fills the width it is given.
//
// Standard 1.2: A COLUMN SET HAS A SHAPE, NOT A SIZE.
//
// A config sets each column's width, and for years that width was taken as
// a promise: thirteen columns at 120px drew 1560px of grid and left 400px of
// empty card to the right of it on a wide screen, or ran off the edge on a
// narrow one. Neither is what the admin meant. What they meant is the
// PROPORTION — the summary is wide, the status is middling, the count is
// narrow — and a proportion can be honoured at any width.
//
// So the widths are read as ratios and fitted to the space available:
//
//   room to spare  every column grows by its share of the slack, and the
//                  set ends EXACTLY at the right-hand edge
//   not enough     with `shrink` off (no chart beside it) nothing moves and
//                  the grid scrolls sideways, which is what a reader expects
//                  of a wide table; with `shrink` on (a timeline is sharing
//                  the width) the set is scaled down, but no column below
//                  `min` — past that a heading is unreadable and the column
//                  is a lie rather than a squeeze.
//
// Everything here is pure arithmetic over {id, width} objects, so it holds
// for any grid in the fork and it runs under node in the suite.

// Below this a heading truncates to nothing and a date will not fit. A
// column already narrower than this was deliberately narrow, so its own
// width is its floor.
export const MIN_COL = 56;
// The narrowest the elastic (Summary) column is made to give way to — see
// fitColumns' `fill`.
export const FILL_MIN = 160;

const widthOf = (c) => Math.max(1, Number(c && c.width) || 0);
export const totalWidth = (cols) => (cols || []).reduce((a, c) => a + widthOf(c), 0);

// The narrowest the set can honestly be drawn — what `clampSplit` will not
// let a divider cross.
export const floorWidth = (cols, min = MIN_COL) =>
  (cols || []).reduce((a, c) => a + Math.min(widthOf(c), min), 0);

// A COLUMN SOMEBODY SIZED ON PURPOSE IS NOT RESIZED AGAIN.
//
// Fitting the whole set to the width available means every column's drawn
// width depends on every other one's — so dragging one heading moved all the
// rest, which is not what "resize this column" means to anybody. A reader who
// has set a width has answered the question for that column; the fit is for
// the columns nobody has spoken for.
//
// `locked` is the set of column ids with an explicit width. They are drawn at
// exactly that width and take no part in the arithmetic; the slack (or the
// squeeze) is shared out among the rest. When every column is locked the set
// is drawn at its own total and any leftover is simply left — the reader
// chose those widths, and quietly undoing them to fill the page would be the
// same bug in a nicer suit.
export const lockedOf = (cols, locked) => new Set(
  locked instanceof Set ? [...locked]
    : (Array.isArray(locked) ? locked : Object.keys(locked || {})).map(String),
);

export function fitColumns(cols, avail, { min = MIN_COL, shrink = false, locked = null, want = null, fill = null } = {}) {
  let list = (cols || []).map((c) => ({ ...c, width: widthOf(c) }));
  if (!list.length || !Number.isFinite(avail) || avail <= 0) return list;

  // THE MOMENT A READER SIZES ANYTHING, THE FITTING STOPS.
  //
  // Sharing the slack among the columns nobody had touched was the obvious
  // half-measure, and it is still wrong: widening one column has to take
  // those pixels from somewhere, so the others still move — which is exactly
  // what "dragging one column should not influence the others" rules out.
  //
  // So the fit is what happens BEFORE anyone has an opinion. Once there is
  // one, every column is drawn at exactly its own width and the grid scrolls
  // if the total is wider than the box. Predictable beats tidy: a reader who
  // has set widths wants those widths, not an arrangement of them.
  //
  // EXCEPT THE ONE ELASTIC COLUMN, when `fill` names it (the Summary, as in
  // a structure). A set narrower than the box would stop short and leave
  // the rest blank, so that column takes the difference. Beside a chart
  // (`shrink`) it also GIVES: the columns must end at the divider, or a set
  // sized on a wide screen covers the whole chart on a narrow one and the
  // divider moves nothing — so the elastic column gives back down to
  // FILL_MIN first, and only if that is not enough are the rest squeezed in
  // proportion. Every other width is the reader's until that point. With
  // no chart beside them a set wider than the box still simply scrolls.
  if (lockedOf(cols, locked).size) {
    const i = fill != null ? list.findIndex((c) => String(c.id) === String(fill)) : -1;
    const total = totalWidth(list);
    if (i >= 0 && total < avail) list[i] = { ...list[i], width: list[i].width + (avail - total) };
    if (i >= 0 && shrink && total > avail) {
      const give = Math.min(total - avail, Math.max(0, list[i].width - FILL_MIN));
      list[i] = { ...list[i], width: list[i].width - give };
    }
    // Still too wide: the elastic column holds at its floor and the OTHERS
    // share the squeeze — a Summary crushed to a sliver while Status keeps
    // its full width is the wrong way round. Only when even they cannot give
    // enough is every column squeezed together.
    // (A set with no elastic column keeps the old rule: its widths, and the
    // grid scrolls.)
    if (i >= 0 && shrink && totalWidth(list) > avail) {
      const rest = list.filter((_, j) => j !== i);
      const room = avail - list[i].width;
      if (room >= floorWidth(rest, min)) {
        const fitted = squeeze(rest, room, min);
        let k = 0;
        return list.map((c, j) => (j === i ? c : fitted[k++]));
      }
      return squeeze(list, avail, min);
    }
    return list;
  }

  // A COLUMN THAT KNOWS WHAT IT HOLDS ASKS FOR THAT MUCH — see
  // `treeColumnWidth`. The ask is taken before the slack is shared, so the
  // text is shown whole first and the proportions are honoured over what is
  // left; it is never taken BELOW the config's own width, which is the
  // admin's floor for the column.
  if (want && typeof want === 'object') {
    list = list.map((c) => {
      const w = Number(want[c.id]);
      return Number.isFinite(w) && w > c.width ? { ...c, width: Math.round(w) } : c;
    });
  }

  const total = totalWidth(list);
  if (total === avail) return list;
  if (total < avail) return grow(list, avail, total);
  if (!shrink) return list;
  return squeeze(list, avail, min);
}

// The slack, shared out by each column's share of the whole — so a column
// twice as wide gains twice as much and the proportions survive. The LAST
// column takes the rounding, so the set lands on `avail` to the pixel
// rather than one short of it (a one-pixel gap at the right-hand edge is
// visible, and it moves as the window moves).
function grow(list, avail, total) {
  const slack = avail - total;
  let spent = 0;
  const out = list.map((c, i) => {
    if (i === list.length - 1) return { ...c, width: c.width + (slack - spent) };
    const add = Math.floor((c.width / total) * slack);
    spent += add;
    return { ...c, width: c.width + add };
  });
  return out;
}

// Scaled down, with a floor. A column pinned at its floor stops taking part
// and the rest absorb what it would not give up — repeated until nothing
// more hits the floor, which is at most once per column.
function squeeze(list, avail, min) {
  const floorOf = (c) => Math.min(c.width, min);
  const pinned = new Set();
  for (let pass = 0; pass <= list.length; pass += 1) {
    const fixed = list.reduce((a, c, i) => (pinned.has(i) ? a + floorOf(c) : a), 0);
    const free = list.reduce((a, c, i) => (pinned.has(i) ? a : a + c.width), 0);
    const room = avail - fixed;
    if (free <= 0 || room <= 0) break;
    const scale = room / free;
    const newly = [];
    list.forEach((c, i) => { if (!pinned.has(i) && c.width * scale < floorOf(c)) newly.push(i); });
    if (!newly.length) {
      // Nothing else bottoms out: apply the scale and give the rounding to
      // the last unpinned column, for the same reason `grow` does.
      let spent = 0;
      const free2 = [];
      list.forEach((c, i) => { if (!pinned.has(i)) free2.push(i); });
      return list.map((c, i) => {
        if (pinned.has(i)) return { ...c, width: floorOf(c) };
        if (i === free2[free2.length - 1]) return { ...c, width: Math.max(floorOf(c), room - spent) };
        const w = Math.max(floorOf(c), Math.floor(c.width * scale));
        spent += w;
        return { ...c, width: w };
      });
    }
    newly.forEach((i) => pinned.add(i));
  }
  // Every column is at its floor: the set is as narrow as it can be drawn,
  // and it is WIDER than `avail`. Saying so by returning the floors is
  // honest — the caller's divider is clamped so this is not reachable by
  // dragging, only by a window too small for the columns at all.
  return list.map((c) => ({ ...c, width: floorOf(c) }));
}

// ---- the tree column fits what it holds --------------------------------------
// Standard 1.11: THE TREE COLUMN SHOWS ITS TEXT WHOLE.
//
// A summary is the one cell a reader cannot do without, and it was the one
// cell that opened truncated: the config gave it 340px, the fit shared the
// slack out by proportion, and "CNC Code Verification - PO79827 - 10St25/1…"
// is not a summary anybody can act on. A structure is READ down its tree
// column; a tree column that has to be widened by hand before it can be read
// is a page that opens broken.
//
// So the tree column asks for the width its rows need — measured, not
// guessed — and `fitColumns` takes that ask before it shares the slack.
// `measure(text, level)` is handed in (a canvas at a desk, a stub in the
// suite) so this stays arithmetic. `cap` keeps one very long summary from
// taking the whole grid: past it the text truncates, and the reader can
// drag the edge for the rest.
//
// The chrome is what sits beside the text in a tree cell: the indent step
// per level, the twisty, the create "+", the type icon and their gaps, the
// cell's own padding, and the "· N" tag a shut row carries.
export const TREE_INDENT = 4;
export const TREE_STEP = 15;
export const TREE_CHROME = 78;

export function treeColumnWidth(rows, measure, {
  cap = Infinity, min = 0, indent = TREE_INDENT, step = TREE_STEP, chrome = TREE_CHROME,
  // The widest a glyph can be at the cell's size — an upper bound, so a row
  // that cannot beat the current answer is skipped without being measured.
  // "Expand all" puts thirty thousand rows in this list; measuring every
  // one of them would be most of the click.
  maxChar = 9,
} = {}) {
  let need = 0;
  for (let i = 0; i < (rows || []).length; i += 1) {
    const r = rows[i];
    if (!r || r.kind === 'rule') continue;
    const text = r.summary != null ? String(r.summary) : String((r.it && r.it.fields && r.it.fields.summary) || '');
    if (!text) continue;
    const base = indent + (Number(r.level) || 0) * step + chrome;
    if (base + text.length * maxChar <= need) continue;
    const w = base + measure(text, r.level);
    if (w > need) need = w;
    if (need >= cap) return Math.round(Math.max(min, cap));
  }
  return Math.round(Math.max(min, Math.min(cap, need)));
}

// ---- the divider -------------------------------------------------------------
// Where a reader may put the line between the columns and the chart beside
// them. Neither side may be squeezed out of existence: the columns keep at
// least their floors, the chart keeps enough width to show a bar and a
// couple of tick labels.
export const MIN_CHART = 220;

// `max` is the widest the columns may be given, whatever the divider says —
// normally their own configured total.
//
// WHY A DIVIDER CANNOT MAKE A COLUMN WIDER THAN IT ASKED TO BE. Dragging it
// right used to STRETCH the set to fill: five columns adding up to 782px got
// spread across 1 120, the summary column ballooned, and the chart was left
// with a quarter of the grid — which is what "the Gantt does not notice the
// left panel exists" was pointing at. The columns' widths are the admin's
// answer to "how much room does this column need"; a reader dragging a
// divider is not overruling that, they are deciding how much of the REST the
// chart gets. So the divider stops where the columns stop, and everything
// past it belongs to the chart. To make a column wider, drag ITS edge.
export function clampSplit(px, { cols, boxW, min = MIN_COL, minChart = MIN_CHART, max = null } = {}) {
  const lo = floorWidth(cols, min);
  const ceiling = Math.min(
    (Number(boxW) || 0) - minChart,
    Number.isFinite(max) && max > 0 ? max : Infinity,
  );
  const hi = Math.max(lo, ceiling);
  const want = Number.isFinite(px) ? px : lo;
  return Math.round(Math.max(lo, Math.min(hi, want)));
}

// The divider is remembered as a FRACTION of the grid, not as a pixel count:
// a reader who splits a 1900px window half and half and then opens the same
// page on a laptop wants half and half there too, not 950px of columns in a
// 1400px window.
export const splitFraction = (px, boxW) => (boxW > 0 ? Math.max(0.05, Math.min(0.95, px / boxW)) : 0.5);
export const splitPixels = (frac, boxW) => Math.round((Number(frac) > 0 ? Number(frac) : 0.5) * (Number(boxW) || 0));

// ---- a heading is READ, not truncated ----------------------------------------
// Standard 1.5: A COLUMN HEADING NEVER ENDS IN AN ELLIPSIS.
//
// "Estimated Time (Hou…" tells a reader nothing they did not already guess,
// and it tells them nothing about the column NEXT to it either — which is
// the whole job of a heading row. Two things fix it and neither is truncation:
// the heading WRAPS to a second line, and if it still does not fit, the type
// gets smaller until it does.
//
// The arithmetic is deliberately crude, because it has to run for every
// heading on every render and the answer only has to be close: an upper-case
// heading at this weight averages about 0.62em a character. Measuring text
// properly means a canvas per render, which is a lot of machinery to choose
// between 11px and 9px.
export const HEAD_MAX = 11;      // the size a heading wants to be
export const HEAD_MIN = 8.5;     // below this it is decoration, not a label
export const HEAD_LINES = 2;     // what a heading normally gets
export const HEAD_LINES_MAX = 3; // and what a very long one is given instead
const CHAR_EM = 0.62;            // average glyph width, upper-case, 700 weight

const roomOf = (width, padding) => Math.max(8, (Number(width) || 0) - padding);
const longestWord = (text) => text.split(/\s+/).reduce((a, w) => Math.max(a, w.length), 0);

// The size that fits `text` into `lines` lines of `room`, unclamped.
const wants = (text, room, lines) => Math.min(
  // the whole label, spread over the lines it is given
  (room * lines) / (Math.max(1, text.length) * CHAR_EM),
  // AND the single longest word on ONE of them — a word cannot wrap inside
  // itself, so it sets the floor whatever the rest of the label does. This is
  // what stops a heading overflowing sideways after it has been wrapped.
  room / (Math.max(1, longestWord(text)) * CHAR_EM),
);

// HOW MANY LINES THIS HEADING NEEDS. Two normally; three when two at the
// smallest legible size still would not hold it — at which point clamping
// would put back the very ellipsis this exists to remove. Three lines of
// 8.5px is 29px, which still fits inside a 38px heading row.
export function headingLines(label, width, { min = HEAD_MIN, padding = 24 } = {}) {
  const text = String(label || '');
  if (!text) return HEAD_LINES;
  return wants(text, roomOf(width, padding), HEAD_LINES) < min ? HEAD_LINES_MAX : HEAD_LINES;
}

export function headingFontSize(label, width, { max = HEAD_MAX, min = HEAD_MIN, lines = null, padding = 24 } = {}) {
  const text = String(label || '');
  if (!text) return max;
  const n = lines || headingLines(label, width, { min, padding });
  return Math.max(min, Math.min(max, wants(text, roomOf(width, padding), n)));
}
